import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import type { InformationHeroBanner as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { originalUrl } from "../../utils/wagtail";

import heroStyles from "../../styles/composable/hero.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/hero_banners/hero_banner.html`` (#33285).
 *
 * - ``id="hero-whypp"`` only when ``presentation == 'robust'``.
 * - The content branches on ``presentation == 'normal'`` with an ``else``
 *   (so the robust markup is also the fallback for a null presentation),
 *   exactly like the legacy ``{% if %}/{% else %}``.
 * - The robust logo's ``alt="Posturpedic Technology"`` typo is legacy
 *   verbatim.
 */
export const InformationHeroBanner = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const value = props.value;
    const robust = value.presentation === "robust";
    return (
        <section className="hero" id={robust ? "hero-whypp" : undefined}>
            <picture>
                <source
                    srcSet={originalUrl(value.mobile_image)}
                    media="(max-width: 768px)"
                />
                <img
                    className={heroStyles.heroBackground}
                    srcSet={originalUrl(value.image)}
                    alt={value.image?.default_alt_text ?? ""}
                />
            </picture>

            <div
                className={concatClassNames([
                    heroStyles.heroContent,
                    "container",
                ])}
            >
                {value.presentation === "normal" ? (
                    <>
                        <h1 className={heroStyles.heroHeadline}>
                            {value.title}
                        </h1>
                        <p
                            className={concatClassNames([
                                heroStyles.heroCopy,
                                "alt-copy",
                            ])}
                        >
                            {value.subtitle}
                        </p>
                    </>
                ) : (
                    <>
                        {value.logo && (
                            <img
                                className={concatClassNames([
                                    heroStyles.heroLogo,
                                    heroStyles.heroLogoSmall,
                                ])}
                                src={`${staticURL}img/hero/logo-posturepedic.svg`}
                                alt="Posturpedic Technology"
                            />
                        )}
                        <h3
                            className={concatClassNames([
                                heroStyles.heroHeadline,
                                heroStyles.heroHeadlineInverse,
                            ])}
                        >
                            {value.title}
                        </h3>
                        <p
                            className={concatClassNames([
                                heroStyles.heroCopy,
                                heroStyles.heroCopyInverse,
                                "alt-copy",
                            ])}
                        >
                            {value.subtitle}
                        </p>
                    </>
                )}
            </div>
        </section>
    );
};
